import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

const PHONE = "@media (max-width: 599.95px)";

// Data table: column headings, one row per item, hairlines between rows. The primary cell is 14 ink,
// every other value 13 ink-2 (secondary text), so the title of each row stands out.
// columns = [{ key, label, render(row), width?, align?, primary?, action? }]
//   width: grid track on wide screens (default "minmax(0, 1fr)"); an action column defaults to one
//   40px control (a ⋯ button) — a fixed track, so the heading row and every row line up exactly.
//   primary: the main cell (e.g. the title). action: the row's buttons / ⋯ menu (no heading text).
// Phones: no heading row; the primary cell and the actions share the first line and every other
// value sits under them with its column label, so each value still says what it is.
export default function GrDataTable({ columns, rows, getRowKey, ariaLabel, sx }) {
  const template = columns
    .map((c) => c.width || (c.action ? gr.control.md : "minmax(0, 1fr)"))
    .join(" ");
  const rowGridSx = {
    display: "grid",
    gridTemplateColumns: template,
    alignItems: "center",
    columnGap: gr.space[4],
  };
  return (
    <Box role="table" aria-label={ariaLabel} sx={{ minWidth: 0, ...sx }}>
      <Box
        role="row"
        sx={{
          ...rowGridSx,
          pb: gr.space[2],
          borderBottom: `1px solid ${gr.color.line}`,
          [PHONE]: { display: "none" },
        }}
      >
        {columns.map((c) => (
          <Box
            key={c.key}
            role="columnheader"
            sx={{
              fontFamily: gr.font.family,
              fontSize: gr.font.size.xs,
              fontWeight: gr.font.weight.medium,
              color: gr.color.muted,
              textAlign: c.align || "left",
              minWidth: 0,
            }}
          >
            {c.action && !c.showLabel ? (
              <Box component="span" sx={visuallyHidden}>{c.label || "Actions"}</Box>
            ) : (
              c.label
            )}
          </Box>
        ))}
      </Box>
      {rows.map((row, index) => (
        <Box
          key={getRowKey ? getRowKey(row, index) : index}
          role="row"
          sx={{
            ...rowGridSx,
            py: gr.space[3],
            "& + &": { borderTop: `1px solid ${gr.color.line2}` },
            [PHONE]: {
              gridTemplateColumns: "minmax(0, 1fr) auto",
              rowGap: gr.space[2],
            },
          }}
        >
          {columns.map((c) => (
            <Box
              key={c.key}
              role="cell"
              sx={{
                minWidth: 0,
                fontFamily: gr.font.family,
                // The main cell (title) reads first at 14 ink; the other values are secondary text, 13 ink-2.
                fontSize: c.primary ? gr.font.size.md : gr.font.size.sm,
                color: c.primary ? gr.color.ink : gr.color.ink2,
                textAlign: c.align || "left",
                display: c.action ? "flex" : "block",
                justifyContent: c.action ? "flex-end" : undefined,
                [PHONE]: c.primary
                  ? { gridColumn: "1", order: 0 }
                  : c.action
                    ? { gridColumn: "2", order: 1, alignSelf: "start" }
                    : {
                        gridColumn: "1 / -1",
                        order: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: gr.space[3],
                        textAlign: "right",
                      },
              }}
            >
              {!c.primary && !c.action ? (
                <Box
                  component="span"
                  sx={{
                    display: "none",
                    fontSize: gr.font.size.sm,
                    color: gr.color.muted,
                    textAlign: "left",
                    [PHONE]: { display: "inline" },
                  }}
                >
                  {c.label}
                </Box>
              ) : null}
              {c.render(row, index)}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

GrDataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      render: PropTypes.func.isRequired,
      width: PropTypes.string,
      align: PropTypes.oneOf(["left", "right"]),
      primary: PropTypes.bool,
      action: PropTypes.bool,
      // Show the action column's heading (e.g. "Action"); hidden by default (screen readers still get it).
      showLabel: PropTypes.bool,
    })
  ).isRequired,
  rows: PropTypes.array.isRequired,
  getRowKey: PropTypes.func,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};
